/**
 * Build & Learn Chord.
 *
 * Pick a clef, write on the staff the way you would on paper, and the app reads
 * what you wrote as you write it: pitches appear beside your ink, vertically
 * aligned noteheads become a chord, and the chord gets named, explained, engraved,
 * and played. No "submit" button — writing is the input.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WritingPad, type WritingPadHandle } from '../components/WritingPad';
import { Score } from '../components/Score';
import { MyWork } from '../components/MyWork';
import { SaveStatus } from '../components/SaveStatus';
import { BackupControls } from '../components/BackupControls';
import { inkForLayout, useDocumentSession } from '../storage/useDocumentSession';
import { suggestTitle } from '../storage/documents';
import type { DocumentPayload } from '../storage/types';
import type { Stroke } from '../ink/types';
import { Button } from '../design/Button';
import { SegmentedControl } from '../design/Navigation';
import type { RecognitionResult } from '../ink/recognize';
import { GLYPH_LABELS, SUPPORTED_GLYPHS } from '../ink/recognize';
import { CLEFS, CLEF_INFO, positionToPitch, type Clef } from '../theory/staff';
import {
  KEY_BY_ID,
  KEY_SIGNATURES,
  accidentalOrder,
  accidentalSummary,
  keyLabel,
} from '../theory/keys';
import { analyzeChord, type ChordAnalysis } from '../theory/chords';
import { detectKey } from '../theory/keyDetection';
import { intervalName, midi, pitchName, pitchNameWithOctave, type Pitch } from '../theory/pitch';
import { playPitches, stop, type PlayMode } from '../audio/player';
import { createInkSession } from '../ink/session';

export interface BuildLearnProps {
  /** A first-class writing workspace, not a second pad mounted over this one. */
  studio?: boolean;
  onOpenStudio?: () => void;
  onCloseStudio?: () => void;
}

export function BuildLearn({ studio = false, onOpenStudio, onCloseStudio }: BuildLearnProps) {
  const padRef = useRef<WritingPadHandle>(null);
  /**
   * The document page behind the one live engine.
   *
   * Manuscript Studio is a route-level layout state, not a second component.
   * This session still owns persistence and reflow, but opening the studio no
   * longer detaches or replaces its canvas.
   */
  const sessionRef = useRef(createInkSession());
  const [clef, setClef] = useState<Clef>('treble');
  const [keyId, setKeyId] = useState('C');
  /**
   * When true the key follows the writing; any manual choice turns it off.
   *
   * On by default. A fresh staff has nothing on it, so there is no key to
   * assert — and asserting C major meant every accidental a learner wrote was
   * being read against a signature they had never picked.
   */
  const [autoKey, setAutoKey] = useState(true);
  const [result, setResult] = useState<RecognitionResult | null>(null);
  const [showWhy, setShowWhy] = useState(false);
  const [chosenIndex, setChosenIndex] = useState(0);
  const [playMode, setPlayMode] = useState<PlayMode>('both');
  const [playbackMessage, setPlaybackMessage] = useState('');
  const playbackTimer = useRef<number | null>(null);
  const [showWork, setShowWork] = useState(false);
  useEffect(() => {
    if (!studio) return;
    const leave = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseStudio?.();
    };
    document.addEventListener('keydown', leave);
    return () => {
      document.removeEventListener('keydown', leave);
    };
  }, [studio, onCloseStudio]);

  useEffect(
    () => () => {
      if (playbackTimer.current) window.clearTimeout(playbackTimer.current);
    },
    [],
  );

  /**
   * Ink waiting for a staff to land on.
   *
   * A document is loaded from storage in staff-relative units, but converting it
   * to pixels needs the layout the pad is actually using — and on first render
   * the pad has not measured itself yet. The payload is parked here and applied
   * as soon as a layout exists.
   */
  const pendingRef = useRef<DocumentPayload | null>(null);

  const applyDocument = useCallback((payload: DocumentPayload) => {
    setClef(payload.clef);
    setKeyId(payload.keyId);
    setAutoKey(payload.autoKey);
    setChosenIndex(0);
    pendingRef.current = payload;
    tryApplyPending();
  }, []);

  /** Put parked ink on the staff once the pad has reported its geometry. */
  const tryApplyPending = useCallback(() => {
    const payload = pendingRef.current;
    const pad = padRef.current;
    const layout = pad?.getLayout() ?? null;
    if (!payload || !pad || !layout) return;
    pendingRef.current = null;
    pad.restore(inkForLayout(payload, layout), payload.overrides);
  }, []);

  const labelsForTitle = useRef<(string | null)[]>([]);

  const session = useDocumentSession({
    feature: 'build',
    snapshot: () => {
      const pad = padRef.current;
      return {
        clef,
        keyId,
        autoKey,
        strokes: (pad?.getStrokes() ?? []) as Stroke[],
        overrides: pad?.getOverrides() ?? {},
        layout: pad?.getLayout() ?? null,
      };
    },
    apply: applyDocument,
    suggestTitle: () => suggestTitle(labelsForTitle.current),
  });

  const { touch, ready } = session;

  // The title field is uncontrolled while typing; it re-syncs whenever the
  // document itself changes, which is how opening another one updates it.
  const [titleDraft, setTitleDraft] = useState('');
  const documentId = session.document?.id ?? null;
  const documentTitle = session.document?.title ?? '';
  useEffect(() => {
    setTitleDraft(documentTitle);
    // Keyed on the id as well: two documents can share a title.
  }, [documentId, documentTitle]);

  // Ink that arrived before the staff had measured itself.
  useEffect(() => {
    if (pendingRef.current) tryApplyPending();
  });

  // Clef and key are part of the document, so changing them is a change to save.
  useEffect(() => {
    if (ready) touch();
  }, [clef, keyId, autoKey, ready, touch]);

  const groups: Pitch[][] = useMemo(
    () => (result?.groups ?? []).map((group) => group.map((n) => n.pitch)),
    [result],
  );

  const analyses: ChordAnalysis[][] = useMemo(
    () => groups.map((group) => analyzeChord(group)),
    [groups],
  );

  /**
   * Evidence for key detection is taken from the *ink*, not from the pitches after
   * the key signature has been applied. Otherwise detection would read back its own
   * output — apply G major, every F becomes F♯, and the F♯ then "proves" G major.
   */
  const evidence: Pitch[] = useMemo(
    () =>
      (result?.notes ?? []).map((note) =>
        positionToPitch(clef, note.position, note.writtenAccidental ?? 0),
      ),
    [result, clef],
  );

  const detectedChords = useMemo(
    () => analyses.map((list) => list[0]).filter((c): c is ChordAnalysis => Boolean(c)),
    [analyses],
  );

  const detection = useMemo(
    () => detectKey(evidence, detectedChords),
    [evidence, detectedChords],
  );

  // In auto mode the key follows detection, but only once the evidence is strong
  // enough — until then the staff stays in C rather than flickering as notes land.
  const effectiveKeyId = autoKey ? (detection.best?.signature.id ?? 'C') : keyId;
  const keySignature = KEY_BY_ID[effectiveKeyId];

  // The chord currently under discussion: the last one written, since that is what
  // the learner just did.
  const focusIndex = groups.length - 1;
  const focusGroup = focusIndex >= 0 ? groups[focusIndex] : [];
  const focusCandidates = focusIndex >= 0 ? analyses[focusIndex] : [];
  const focus = focusCandidates[Math.min(chosenIndex, focusCandidates.length - 1)] ?? null;

  const labels = analyses.map((list) => (list.length ? list[0].name : null));
  labelsForTitle.current = labels;

  const handleResult = (next: RecognitionResult) => {
    setResult(next);
    setChosenIndex(0);
    // Recognition runs after a stroke settles, which is exactly the moment the
    // page is worth saving — and it is off the ink path by construction.
    if (ready) touch();
  };

  const sortedFocus = useMemo(
    () => [...focusGroup].sort((a, b) => midi(a) - midi(b)),
    [focusGroup],
  );

  const chooseKey = (value: string) => {
    if (value === 'auto') {
      setAutoKey(true);
    } else {
      setAutoKey(false);
      setKeyId(value);
    }
  };

  const hear = async (pitches: Pitch[], mode: PlayMode, label = 'Playing premium samples') => {
    if (!pitches.length) return;
    if (playbackTimer.current) window.clearTimeout(playbackTimer.current);
    setPlaybackMessage('Preparing premium samples…');
    await playPitches(pitches, mode);
    setPlaybackMessage(label);
    playbackTimer.current = window.setTimeout(() => setPlaybackMessage(''), 1800);
  };

  const intervals = useMemo(() => {
    if (sortedFocus.length < 2) return [];
    return sortedFocus.slice(1).map((p, i) => ({
      from: sortedFocus[i],
      to: p,
      name: intervalName(sortedFocus[i], p),
    }));
  }, [sortedFocus]);

  const status = (() => {
    if (!result || result.notes.length === 0) return 'Nothing written yet.';
    const n = result.notes.length;
    const g = result.groups.length;
    return `${n} notehead${n === 1 ? '' : 's'} read in ${g} position${g === 1 ? '' : 's'}.`;
  })();

  return (
    <div className={`feature build-feature ${studio ? 'is-studio' : ''}`}>
      <header className="feature-head">
        <div>
          <p className="eyebrow">Build &amp; Learn Chord</p>
          <h2>Write it the way you would on paper.</h2>
          <p className="lede">
            Choose a clef, then draw noteheads on the staff. MusicMentor reads your handwriting as
            you go, names what you built, and shows you why it is called that.
          </p>
        </div>

        <div className="doc-bar">
          <label className="doc-title">
            <span className="sr-only">Document title</span>
            <input
              // Held locally while typing and committed on blur or Enter. Writing
              // on every keystroke would burn a revision per character, and
              // revisions are what conflict detection will compare.
              value={titleDraft}
              placeholder="Untitled"
              aria-label="Document title"
              onChange={(event) => setTitleDraft(event.target.value)}
              onBlur={() => void session.rename(titleDraft)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </label>
          <SaveStatus status={session.status} ephemeral={session.ephemeral} />
          <Button
            variant="tertiary"
            icon="menu"
            aria-expanded={showWork}
            onClick={() => setShowWork((open) => !open)}
          >
            My work
          </Button>
        </div>
      </header>

      {session.restored ? (
        <p className="restored-note" role="status">
          Picked up where you left off — “{session.restored.title}”.{' '}
          <Button
            variant="tertiary"
            onClick={() => {
              void session.newDocument();
              session.acknowledgeRestore();
            }}
          >
            Start something new
          </Button>
        </p>
      ) : null}

      {showWork ? (
        <MyWork
          activeId={session.document?.id ?? null}
          onOpen={(id) => {
            void session.openDocument(id);
            setShowWork(false);
          }}
          onNew={() => {
            void session.newDocument();
            setShowWork(false);
          }}
          onClose={() => setShowWork(false)}
        />
      ) : null}

      <div className="control-row write-setup">
        <SegmentedControl
          label="Clef"
          value={clef}
          onChange={setClef}
          options={CLEFS.map((c) => ({ value: c, label: CLEF_INFO[c].label }))}
        />

        <label className="field">
          <span>Key signature</span>
          <select
            value={autoKey ? 'auto' : keyId}
            onChange={(e) => chooseKey(e.target.value)}
          >
            <option value="auto">Detect from writing</option>
            {KEY_SIGNATURES.map((k) => (
              <option key={k.id} value={k.id}>
                {keyLabel(k)}
              </option>
            ))}
          </select>
        </label>

        {/* The way back. Once a key is chosen by hand the app must never switch
            it behind the learner, so returning to detection has to be something
            they ask for. */}
        {autoKey ? null : (
          <Button variant="tertiary" onClick={() => setAutoKey(true)}>
            Detect from writing
          </Button>
        )}
      </div>

      {autoKey ? (
        <div className="key-detect">
          <p className="key-detect-summary">
            <strong>{detection.sufficient ? keySignature.major : 'No key applied yet'}</strong> —{' '}
            {detection.summary}
          </p>
          {detection.candidates.length ? (
            <div className="key-candidates">
              {detection.candidates.map((candidate) => (
                <button
                  key={candidate.signature.id}
                  type="button"
                  className="mm-note-chip"
                  aria-pressed={detection.sufficient && candidate === detection.best}
                  title={candidate.reasons.join('; ') || 'no distinguishing evidence'}
                  onClick={() => {
                    setAutoKey(false);
                    setKeyId(candidate.signature.id);
                  }}
                >
                  {candidate.signature.major} {Math.round(candidate.confidence * 100)}%
                </button>
              ))}
              <span className="key-detect-hint">
                Tap one to lock it in — a manual choice always overrides detection.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="clef-blurb">
        {CLEF_INFO[clef].blurb}
        {keySignature.count > 0 ? (
          <>
            {' '}
            <span className="key-note">
              {keySignature.major} major writes {accidentalSummary(keySignature)} in this order:{' '}
              {accidentalOrder(keySignature)
                .map((l) => `${l}${keySignature.kind === 'sharp' ? '♯' : '♭'}`)
                .join(' ')}
              . Notes you write on those letters take the accidental automatically.
            </span>
          </>
        ) : null}
      </p>

      <section
        className="manuscript-workspace"
        role={studio ? 'dialog' : undefined}
        aria-modal={studio ? 'true' : undefined}
        aria-label={studio ? `Manuscript Studio — ${clef} staff` : undefined}
      >
        <div className="manuscript-workspace__bar">
          {studio ? (
            <div className="manuscript-workspace__identity">
              <div>
                <p className="eyebrow">MusicMentor</p>
                <h2>Manuscript Studio</h2>
              </div>
              <p className="manuscript-workspace__staff">
                <span aria-hidden="true" className="manuscript-workspace__ready" />
                {CLEF_INFO[clef].label} staff · Live notation
              </p>
              <p className="sr-only">
                Draw one notehead and it snaps to clean notation. Select a recognised note to set
                natural, sharp, or flat.
              </p>
            </div>
          ) : <span />}
          <Button
            variant={studio ? 'primary' : 'secondary'}
            onClick={studio ? onCloseStudio : onOpenStudio}
          >
            {studio ? 'Exit studio' : 'Open Manuscript Studio'}
          </Button>
        </div>
        <WritingPad
          key="single-manuscript-engine"
          ref={padRef}
          session={sessionRef.current}
          studio={studio}
          clef={clef}
          keySignature={effectiveKeyId}
          keySignatureMap={keySignature.map}
          onResult={handleResult}
          toolbarExtra={
            studio ? (
              <div className="studio-deck" aria-label="Studio settings">
                <label className="studio-deck__field">
                  <span>Clef</span>
                  <select value={clef} onChange={(event) => setClef(event.target.value as Clef)}>
                    {CLEFS.map((value) => (
                      <option key={value} value={value}>{CLEF_INFO[value].label}</option>
                    ))}
                  </select>
                </label>
                <label className="studio-deck__field">
                  <span>Key</span>
                  <select value={autoKey ? 'auto' : keyId} onChange={(event) => chooseKey(event.target.value)}>
                    <option value="auto">Auto</option>
                    {KEY_SIGNATURES.map((signature) => (
                      <option key={signature.id} value={signature.id}>{keyLabel(signature)}</option>
                    ))}
                  </select>
                </label>
                <Button
                  variant="primary"
                  icon="play"
                  disabled={!sortedFocus.length}
                  onClick={() => void hear(sortedFocus, 'both', 'Playing arpeggio, then chord')}
                >
                  Hear notes
                </Button>
              </div>
            ) : undefined
          }
          height={studio ? 720 : 360}
          hint={
            studio
              ? 'Draw a small filled oval on a line or space. It snaps fast. Use the note controls below for ♮, ♯, or ♭.'
              : 'Draw a small filled oval on a line or space. Stack three or four for a chord. Use the note controls below for ♮, ♯, or ♭.'
          }
        />
      </section>

      {playbackMessage ? (
        <p className="studio-playback-status" role="status" aria-live="polite">
          <span aria-hidden="true" /> {playbackMessage}
        </p>
      ) : null}

      <p className="status-line">{status}</p>

      <div className="split">
        <section className="panel">
          <h3>What you wrote</h3>

          {/*
            The register, in scientific pitch, always — not only once the notes
            happen to form a nameable chord. It is the one fact a learner needs
            to check the app against their own reading, and it is spelled from
            the very `Pitch` objects that the engraving and the speaker are
            handed, never from a second table.
          */}
          {sortedFocus.length ? (
            <p className="pitch-readout" aria-label="Recognised pitches">
              {sortedFocus.map((pitch, i) => (
                <span key={`spn-${pitchNameWithOctave(pitch)}-${i}`} className="pitch-readout__note">
                  {pitchNameWithOctave(pitch)}
                </span>
              ))}
            </p>
          ) : null}

          {focus ? (
            <>
              <p className="chord-name">{focus.name}</p>
              <p className="chord-sub">
                {focus.quality.name} · {focus.inversionLabel}
              </p>
              <div className="confidence">
                <div className="confidence-bar">
                  <span style={{ width: `${Math.round(focus.confidence * 100)}%` }} />
                </div>
                <span>{Math.round(focus.confidence * 100)}% confident</span>
              </div>

              {focusCandidates.length > 1 ? (
                <div className="candidates">
                  <span>Other readings:</span>
                  {focusCandidates.map((cand, i) => (
                    <button
                      key={cand.name}
                      type="button"
                      className="mm-note-chip"
                      aria-pressed={i === chosenIndex}
                      onClick={() => setChosenIndex(i)}
                    >
                      {cand.name}
                    </button>
                  ))}
                </div>
              ) : null}

              <ul className="degree-list">
                {sortedFocus.map((pitch, i) => (
                  <li key={`${pitchNameWithOctave(pitch)}-${i}`}>
                    <span className="degree">{focus.degrees[i]}</span>
                    <span className="note">{pitchNameWithOctave(pitch)}</span>
                  </li>
                ))}
              </ul>

              {intervals.length ? (
                <p className="intervals">
                  Stacked intervals:{' '}
                  {intervals.map((iv, i) => (
                    <span key={i}>
                      {pitchName(iv.from)}→{pitchName(iv.to)} <strong>{iv.name}</strong>
                      {i < intervals.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </p>
              ) : null}

              <p className="cause">{focus.quality.cause}</p>

              {focus.caveats.map((c) => (
                <p key={c} className="caveat">
                  {c}
                </p>
              ))}

              <div className="play-row">
                <Button variant="primary" icon="play" onClick={() => void hear(sortedFocus, playMode)}>
                  Listen
                </Button>
                <select
                  value={playMode}
                  onChange={(e) => setPlayMode(e.target.value as PlayMode)}
                  aria-label="Playback style"
                >
                  <option value="block">Block chord</option>
                  <option value="arpeggio">Arpeggio</option>
                  <option value="both">Arpeggio then block</option>
                </select>
                <Button variant="tertiary" icon="stop" onClick={stop}>
                  Stop
                </Button>
              </div>
            </>
          ) : focusGroup.length > 0 ? (
            <>
              <p className="chord-name muted">Not a chord yet</p>
              <p className="chord-sub">
                {focusGroup.length === 1
                  ? 'One note. A chord needs at least three.'
                  : focusGroup.length === 2
                    ? `An interval — ${intervals[0]?.name ?? ''} from ${pitchName(sortedFocus[0])} to ${pitchName(sortedFocus[1])}. Add a third note to give it a quality.`
                    : 'These notes do not stack into a quality I know. Check the spelling, or try a different reading.'}
              </p>
              <div className="play-row">
                <Button variant="primary" icon="play" onClick={() => void hear(sortedFocus, 'arpeggio')}>
                  Hear what you wrote
                </Button>
              </div>
            </>
          ) : (
            <p className="empty">Write some noteheads and this panel fills in.</p>
          )}
        </section>

        <section className="panel">
          <h3>Engraved</h3>
          <div className="score-wrap">
            <Score clef={clef} groups={groups} keySignature={effectiveKeyId} labels={labels} height={200} />
          </div>
          <p className="fine">
            When a note is read, your handwriting fades and the engraved note takes its place. The
            strokes are still saved — Undo brings them back, and clearing the pad starts over.
          </p>

          <Button
            variant="secondary"
            block
            aria-expanded={showWhy}
            onClick={() => setShowWhy((v) => !v)}
          >
            {showWhy ? 'Hide' : 'Show'} how it read your ink
          </Button>

          {showWhy ? (
            <div className="why">
              {result && result.glyphs.length ? (
                <ul>
                  {result.glyphs.map((g, i) => (
                    <li key={i} className={g.kind === 'unknown' ? 'is-unknown' : ''}>
                      <strong>{GLYPH_LABELS[g.kind]}</strong> — {g.reason}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="empty">Nothing on the pad yet.</p>
              )}
              <h4>What the recogniser can read</h4>
              <ul className="glyph-matrix">
                {SUPPORTED_GLYPHS.map((g) => (
                  <li key={g.kind}>
                    <strong>{GLYPH_LABELS[g.kind]}</strong> — {g.how}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      </div>

      <section className="panel">
        <h3>Keeping your work</h3>
        <p className="fine">
          Everything you write is saved on this iPad as you go. Signed in, it also syncs to your
          account; signed out, this iPad is the only copy — so keep a backup.
        </p>
        <BackupControls onImported={() => setShowWork(true)} />
      </section>
    </div>
  );
}
